import { IMplServiceClient } from '../api/IMplServiceClient';
import { log } from '../loggers/Logger';
import { TypedEmitter, Unsubscribe } from './TypedEmitter';

export type EnabledFeaturesServiceEvents = {
  changed: () => void;
};

/** Loads and exposes the set of feature ids the backend has enabled for the current user. */
export class EnabledFeaturesService {
  private readonly _emitter = new TypedEmitter<EnabledFeaturesServiceEvents>();
  private _enabledFeatureIds: ReadonlySet<string> = new Set();

  constructor(private readonly _client: IMplServiceClient) {}

  /**
   * Fetches the current user's enabled features from the backend and caches
   * them. Never throws — a failure leaves the cached set unchanged and is
   * logged, so callers treat every feature as disabled until a load succeeds.
   */
  async loadEnabledFeatures(): Promise<void> {
    try {
      const response = await this._client.listEnabledFeatures();
      // When the backend responds, then cache the ids and notify subscribers.
      this._enabledFeatureIds = new Set(response.enabledFeatureIds);
      log.info({ enabledFeatureIds: response.enabledFeatureIds, type: 'ENABLED_FEATURES' });
      this._emitter.emit('changed');
    } catch (error) {
      log.warn({ error, type: 'ENABLED_FEATURES_FAILED' });
    }
  }

  /** The feature ids currently enabled for the user; empty until a load succeeds. */
  get enabledFeatureIds(): ReadonlySet<string> {
    return this._enabledFeatureIds;
  }

  isFeatureEnabled(featureId: string): boolean {
    return this._enabledFeatureIds.has(featureId);
  }

  on<K extends keyof EnabledFeaturesServiceEvents & string>(
    event: K,
    listener: EnabledFeaturesServiceEvents[K],
  ): Unsubscribe {
    return this._emitter.on(event, listener);
  }
}
